// 1. Framework / library imports
import { memo, useCallback, useState } from "react";

// 2. Helpers & Hooks
import { useTranslate } from "helpers";

// 3. API & Types
import {
  createVehiclePictureAPI,
} from "services/api/fleet/vehicles/pictures/api";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";
import { showNotification } from "services/api/snackbar";

// 4. UI Components
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import HorizontalScroll from "ui/components/Scroll/HorizontalScroll/HorizontalScroll";
import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";

// 5. Local Components


// 6. Local Types
import { VehiclePictureEditProps } from "./types";

const VehiclePictureEditCard: React.FC<VehiclePictureEditProps> = (props) => {
  const { show, title, vehicleName, vehicleId, pictureId, onSuccess, onClose } =
    props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pendingUploads, setPendingUploads] = useState<FleetDocumentUpload[]>([]);

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    setIsLoading(true);

    if (!pendingUploads || pendingUploads.length === 0) {
      setIsLoading(false);
      return;
    }

    try {
      const result = await Promise.all(
        pendingUploads.map((file) =>
          createVehiclePictureAPI(vehicleId, file.id, vehicleName, "", t, true)
        )
      );
      if (result && onSuccess) {
        showNotification(
          t("admin.fleet.vehicle.picture.create.message.success"),
          { variant: "success" }
        );
        setPendingUploads([]);
        onSuccess();
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleUploadSave = useCallback(
    (uploads: FleetDocumentUpload[]) => {
      setPendingUploads((prev) => [...prev, ...uploads]);
    },
    []
  );

  return (
    <Modal show={show} onHide={onClose} size="small">
      <ModalHeader
        title={title}
        onHide={onClose}
        icon="/media/images/add-icon.svg"
      />
      <ModalBody>
      <div className="h-screen w-full m-auto">
        <div className=" h-full">
          <form noValidate className="text-left flex-1 p-4">
            <div className="">
              <p className="mb-4 text-sm text-slate-600 ">
                {t("admin.fleet.vehicle.edit.picture.add.description")}
              </p>
              <HorizontalScroll className="mb-4">
                <div className="bg-gray-100 rounded-lg flex items-center justify-center h-40 p-6 w-48 shrink-0 relative group">
                  <img
                    src="/media/images/vehicles/vehicle-1.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-100 rounded-lg flex items-center justify-center h-40 p-6 w-48 shrink-0 relative group">
                  <img
                    src="/media/images/vehicles/vehicle-2.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-100 rounded-lg flex items-center justify-center h-40 p-6 w-48 shrink-0 relative group">
                  <img
                    src="/media/images/vehicles/vehicle-3.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-100 rounded-lg flex items-center justify-center h-40 p-6 w-48 shrink-0 relative group">
                  <img
                    src="/media/images/vehicles/vehicle-4.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-100 rounded-lg flex items-center justify-center h-40 p-6 w-48 shrink-0 relative group">
                  <img
                    src="/media/images/vehicles/vehicle-5.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-100 rounded-lg flex items-center justify-center h-40 p-6 w-48 shrink-0 relative group">
                  <img
                    src="/media/images/vehicles/vehicle-6.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-100 rounded-lg flex items-center justify-center h-40 p-6 w-48 shrink-0 relative group">
                  <img
                    src="/media/images/vehicles/vehicle-7.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-100 rounded-lg flex items-center justify-center h-40 p-6 w-48 shrink-0 relative group">
                  <img
                    src="/media/images/vehicles/vehicle-8.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-gray-100 rounded-lg flex items-center justify-center h-40 p-6 w-48 shrink-0 relative group">
                  <img
                    src="/media/images/vehicles/vehicle-9.png"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              </HorizontalScroll>
              <FileUploadControl
                uploads={pendingUploads}
                onUploadSave={handleUploadSave}
                onUploadDelete={(id) => setPendingUploads(prev => prev.filter(u => u.id !== id))}
                buttonText="Dodaj sliku"
                modalTitle="Upload slika"
              />
            </div>
            <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
              <Button
                variant="outlined"
                children={t("admin.close")}
                onClick={() => onClose()}
              />
              <Button
                variant="primary"
                children={t("admin.save")}
                onClick={handleFormSubmit}
              />
            </div>
          </form>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};
export default memo(VehiclePictureEditCard);
